import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, GeoJSON, useMap, Marker } from 'react-leaflet';
import { Range } from "react-range";
import L from 'leaflet';
import { useFetchRouteMapData } from '../hooks/queries/useFetchRouteMapData';
import { useUploadRoute } from '../hooks/mutations/useUploadRoute';
import { Spinner } from '../components/ui/Spinner';
import RoutePlanner from '../components/routes/RoutePlanner';
import HeatmapOverlay from './HeatmapOverlay';
import 'leaflet/dist/leaflet.css';

/** ========= Custom calendar helpers (ported from VideoList) ========= */
const calendarWeekdayLabels = ["M", "T", "W", "T", "F", "S", "S"];

const toIsoDate = (date) => date.toISOString().split("T")[0];
const parseIsoDate = (value) => {
  if (!value) return null;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
};

const isWithinRange = (day, start, end) => {
  if (!day || !start || !end) return false;
  return day > start && day < end;
};

const buildCalendarMatrix = (year, month) => {
  // Monday-based grid
  const firstOfMonth = new Date(year, month, 1);
  const lastOfMonth = new Date(year, month + 1, 0);

  // Start from Monday of the week containing the 1st
  const start = new Date(firstOfMonth);
  const startOffset = (start.getDay() + 6) % 7; // Mon=0 ... Sun=6
  start.setDate(start.getDate() - startOffset);

  // End at Sunday of the week containing the last day of month
  const end = new Date(lastOfMonth);
  const endOffset = 6 - ((end.getDay() + 6) % 7);
  end.setDate(end.getDate() + endOffset);

  const weeks = [];
  let cursor = new Date(start);

  while (cursor <= end) {
    const week = [];
    for (let i = 0; i < 7; i += 1) {
      const date = new Date(cursor);
      const inMonth = date.getMonth() === month;

      week.push({
        date,
        label: String(date.getDate()), // keep numeric; you pad in render
        inMonth,
      });

      cursor.setDate(cursor.getDate() + 1);
    }
    weeks.push(week);
  }

  return weeks;
};

function EmptyRoutesIcon() {
  return (
    <svg width="82" height="82" viewBox="0 0 82 82" fill="none" xmlns="http://www.w3.org/2000/svg" className="opacity-80">
      <g opacity="0.8">
        <path fillRule="evenodd" clipRule="evenodd" d="M54.1351 40.6117L40.5449 54.2006C40.5449 54.2006 45.3778 52.6913 52.3235 55.1065C53.492 55.5126 55.4471 53.5574 55.041 52.3889C52.6258 45.4433 54.1351 40.6117 54.1351 40.6117Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M17.7636 17.8298C26.0149 9.57984 39.4104 9.57984 47.6616 17.8298C55.9116 26.0811 55.9116 39.4765 47.6616 47.7278C39.4104 55.9777 26.0149 55.9777 17.7636 47.7278C9.51368 39.4765 9.51368 26.0811 17.7636 17.8298Z" fill="#E9F3FC"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M44.9279 15.521C45.8863 16.2013 46.8011 16.9714 47.6608 17.8298C55.9108 26.0811 55.9108 39.4765 47.6608 47.7278C39.4096 55.9777 26.0141 55.9777 17.7628 47.7278C17.2773 47.2409 16.8198 46.7374 16.3906 46.2185C24.3882 48.8168 33.5286 46.9411 39.8785 40.5912C46.6512 33.8185 48.3348 23.8709 44.9279 15.521Z" fill="#CADCF0"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M15.9516 16.0182C25.2023 6.76754 40.2224 6.76754 49.473 16.0182C58.7236 25.2688 58.7236 40.2889 49.473 49.5395C40.2224 58.7901 25.2023 58.7901 15.9516 49.5395C6.70102 40.2889 6.70102 25.2688 15.9516 16.0182ZM18.6692 18.7357C26.4195 10.9854 39.0052 10.9854 46.7555 18.7357C54.5057 26.486 54.5057 39.0717 46.7555 46.822C39.0052 54.5723 26.4195 54.5723 18.6692 46.822C10.9189 39.0717 10.9189 26.486 18.6692 18.7357Z" fill="#A4BBDB"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M11.4763 43.319C7.07904 34.454 8.57042 23.3994 15.9517 16.0181C22.8833 9.08655 33.0538 7.34918 41.6113 10.806C41.6728 11.5914 41.7048 12.3871 41.7048 13.1904C41.7048 13.8105 41.6856 14.4268 41.6485 15.0392C34.2031 11.2903 24.8833 12.5229 18.6692 18.7356C12.0823 25.3225 11.0932 35.4021 15.7044 43.0384C14.354 43.2229 12.9754 43.319 11.5762 43.319H11.4763Z" fill="#B9CAE3"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M51.2841 59.5051C49.0329 57.2539 49.0329 53.6036 51.2841 51.3512C53.5352 49.1001 57.1868 49.1001 59.4379 51.3512L78.0109 69.9242C78.5106 70.4239 77.0897 72.6571 74.8398 74.907C72.59 77.1569 70.3568 78.5778 69.8571 78.0781L51.2841 59.5051Z" fill="#A4BBDB"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M51.2841 59.5051C49.0329 57.2539 49.0329 53.6036 51.2841 51.3512C53.5352 49.1001 57.1868 49.1001 59.4379 51.3512L76.2479 68.1612C76.7476 68.6609 75.3267 70.8941 73.0768 73.144C70.827 75.3939 68.5938 76.8148 68.0941 76.3151L51.2841 59.5051Z" fill="#CADCF0"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M29.9982 28.7448C29.9687 27.5238 30.4274 26.632 31.1347 26.1093C32.2468 25.2893 33.9086 25.3239 34.8106 26.3399C35.1975 26.7768 35.4217 27.3944 35.4281 28.1759C35.4435 30.0504 34.5646 31.1459 33.7036 32.2798C32.5453 33.8083 31.3883 35.3791 31.2448 38.0018C31.2064 38.7091 31.7497 39.3125 32.4556 39.351C33.1616 39.3894 33.7664 38.8474 33.8048 38.1415C33.9124 36.1491 34.8657 34.9883 35.7459 33.8275C36.9246 32.2734 38.0124 30.723 37.9906 28.1554C37.9778 26.6461 37.4743 25.4814 36.726 24.6384C34.9784 22.6704 31.7689 22.4577 29.6138 24.0464C28.3223 24.9997 27.3832 26.5782 27.4357 28.805C27.4523 29.5123 28.0404 30.0722 28.7477 30.0555C29.4537 30.0389 30.0148 29.452 29.9982 28.7448Z" fill="#8DA5CF"/>
        <path d="M32.406 44.1095C33.2764 44.1095 33.982 43.4039 33.982 42.5336C33.982 41.6632 33.2764 40.9576 32.406 40.9576C31.5356 40.9576 30.8301 41.6632 30.8301 42.5336C30.8301 43.4039 31.5356 44.1095 32.406 44.1095Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M55.4638 10.8867L59.3076 4.48048C59.6715 3.87445 59.4754 3.08648 58.8681 2.72261C58.2621 2.36001 57.4741 2.55604 57.1103 3.16207L53.2665 9.56833C52.9026 10.1756 53.0999 10.9636 53.706 11.3262C54.3133 11.6901 55.1 11.494 55.4638 10.8867Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M61.5248 15.1084L75.6185 4.85845C76.1899 4.44204 76.3168 3.63998 75.9017 3.06854C75.4853 2.49582 74.6832 2.37026 74.1118 2.78539L60.018 13.0354C59.4453 13.4518 59.3197 14.2539 59.7349 14.8253C60.1513 15.398 60.9533 15.5236 61.5248 15.1084Z" fill="#8DA5CF"/>
        <path fillRule="evenodd" clipRule="evenodd" d="M63.6442 23.0022L73.8942 20.4397C74.5809 20.268 74.9986 19.5723 74.8269 18.8856C74.6552 18.2001 73.9595 17.7824 73.2728 17.9541L63.0228 20.5166C62.3373 20.6883 61.9196 21.384 62.0913 22.0708C62.263 22.7562 62.9587 23.1739 63.6442 23.0022Z" fill="#8DA5CF"/>
      </g>
    </svg>
  );
}

// Component to fit map bounds to all routes
function FitBoundsToRoutes({ routes }) {
  const map = useMap();

  useEffect(() => {
    if (!routes || routes.length === 0) return;

    const bounds = L.latLngBounds([]);
    routes.forEach((route) => {
      if (route.geom) {
        const layer = L.geoJSON(route.geom);
        bounds.extend(layer.getBounds());
      }
    });

    if (bounds.isValid()) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [routes, map]);

  return null;
}

function RouteMiniPopup({ route, latlng, onClose }) {
  const map = useMap();
  const [pos, setPos] = React.useState({ left: 0, top: 0 });

  React.useEffect(() => {
    if (!route || !latlng) return;

    const update = () => {
      const p = map.latLngToContainerPoint(latlng);
      setPos({ left: p.x, top: p.y });
    };

    update();
    map.on("move", update);
    map.on("zoom", update);

    return () => {
      map.off("move", update);
      map.off("zoom", update);
    };
  }, [map, route, latlng]);

  if (!route || !latlng) return null;

  const uploaded = new Date(route.uploaded_at);
  const dateStr = uploaded.toLocaleDateString("en-GB");
  const timeStr = uploaded.toLocaleTimeString("el-GR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  return (
    <div
      className="pointer-events-none absolute z-[5000]"
      style={{
        left: pos.left,
        top: pos.top,
        transform: "translate(-50%, calc(-100% - 12px))", // above click point
      }}
    >
      <div
        className="pointer-events-auto relative w-[201px] h-[120px] bg-[#FFFCFE] border border-[#F6BED8] rounded-[8px]"
        style={{ boxShadow: "0px 4px 4px rgba(0,0,0,0.25)" }}
      >
        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className="absolute right-[8px] top-[8px] w-[16px] h-[16px] flex items-center justify-center text-[#1C1B1F]"
          aria-label="Close"
        >
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
            <path d="M1 1L9 9M9 1L1 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>

        <div className="absolute left-[16px] top-[16px] right-[16px] flex flex-col items-start gap-[4px]">
          {/* Title row */}
          <div className="flex items-center gap-[4px] h-[22px]">
            <div className="text-[14px] font-semibold leading-[155%] text-black">
              {route.name?.trim() ? route.name : `Route #${route.id}`}
            </div>

            {/* bike icon (12x12) */}
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <mask
                id={`bike-mask-${route.id}`}
                style={{ maskType: "alpha" }}
                maskUnits="userSpaceOnUse"
                x="0"
                y="0"
                width="12"
                height="12"
              >
                <rect width="12" height="12" fill="#D9D9D9" />
              </mask>

              <g mask={`url(#bike-mask-${route.id})`}>
                <path
                  d="M2.69038 10.6895C2.08329 10.6895 1.56646 10.4755 1.13988 10.0477C0.713292 9.61987 0.5 9.10241 0.5 8.49533C0.5 7.88824 0.713917 7.37141 1.14175 6.94483C1.56967 6.51824 2.08717 6.30495 2.69425 6.30495C3.30125 6.30495 3.81808 6.51887 4.24475 6.9467C4.67133 7.37454 4.88462 7.89199 4.88462 8.49908C4.88462 9.10616 4.67067 9.623 4.24275 10.0496C3.81492 10.4762 3.29746 10.6895 2.69038 10.6895ZM2.69 10.1895C3.1595 10.1895 3.55929 10.0252 3.88937 9.69658C4.21954 9.36799 4.38462 8.96895 4.38462 8.49945C4.38462 8.03004 4.22029 7.63025 3.89162 7.30008C3.56304 6.97 3.16404 6.80495 2.69463 6.80495C2.22513 6.80495 1.82533 6.96924 1.49525 7.29783C1.16508 7.62641 1 8.02545 1 8.49495C1 8.96437 1.16429 9.36416 1.49288 9.69433C1.82154 10.0244 2.22058 10.1895 2.69 10.1895ZM6.6 3.70108L5.1115 5.18945L6.02313 6.1482C6.09554 6.22062 6.15146 6.30554 6.19088 6.40295C6.23029 6.50037 6.25 6.59974 6.25 6.70108V8.9972C6.25 9.06804 6.226 9.12741 6.178 9.17533C6.13 9.22324 6.07054 9.2472 5.99963 9.2472C5.92871 9.2472 5.86938 9.22324 5.82163 9.17533C5.77388 9.12741 5.75 9.06804 5.75 8.9972V6.77608L4.01538 5.46258C3.94104 5.39658 3.88367 5.31837 3.84325 5.22795C3.80292 5.13762 3.78275 5.04262 3.78275 4.94295C3.78275 4.8432 3.80342 4.75054 3.84475 4.66495C3.88608 4.57937 3.94296 4.50037 4.01538 4.42795L5.47312 2.97033C5.54746 2.89591 5.63467 2.83854 5.73475 2.7982C5.83492 2.75779 5.93992 2.73758 6.04975 2.73758C6.1595 2.73758 6.26454 2.75779 6.36488 2.7982C6.46521 2.83854 6.55254 2.89591 6.62688 2.97033L7.57688 3.92033C7.78888 4.13008 8.02158 4.30108 8.275 4.43333C8.52842 4.56558 8.79796 4.65449 9.08362 4.70008C9.15479 4.71224 9.21233 4.74687 9.25625 4.80395C9.30017 4.86095 9.31604 4.92504 9.30387 4.9962C9.29171 5.06737 9.25708 5.12491 9.2 5.16883C9.14292 5.21274 9.07883 5.22862 9.00775 5.21645C8.65192 5.16262 8.31904 5.05395 8.00912 4.89045C7.69921 4.72704 7.42054 4.52158 7.17312 4.27408L6.6 3.70108ZM7.55775 2.68945C7.34683 2.68945 7.16908 2.6172 7.0245 2.4727C6.88 2.32812 6.80775 2.15037 6.80775 1.93945C6.80775 1.72862 6.88 1.55091 7.0245 1.40633C7.16908 1.26174 7.34683 1.18945 7.55775 1.18945C7.76858 1.18945 7.94629 1.26174 8.09088 1.40633C8.23546 1.55091 8.30775 1.72862 8.30775 1.93945C8.30775 2.15037 8.23546 2.32812 8.09088 2.4727C7.94629 2.6172 7.76858 2.68945 7.55775 2.68945ZM9.30575 10.6895C8.69875 10.6895 8.18192 10.4755 7.75525 10.0477C7.32867 9.61987 7.11538 9.10241 7.11538 8.49533C7.11538 7.88824 7.32933 7.37141 7.75725 6.94483C8.18508 6.51824 8.70254 6.30495 9.30963 6.30495C9.91671 6.30495 10.4335 6.51887 10.8601 6.9467C11.2867 7.37454 11.5 7.89199 11.5 8.49908C11.5 9.10616 11.2861 9.623 10.8582 10.0496C10.4303 10.4762 9.91283 10.6895 9.30575 10.6895ZM9.30538 10.1895C9.77487 10.1895 10.1747 10.0252 10.5048 9.69658C10.8349 9.36799 11 8.96895 11 8.49945C11 8.03004 10.8357 7.63025 10.5071 7.30008C10.1785 6.97 9.77942 6.80495 9.31 6.80495C8.8405 6.80495 8.44071 6.96924 8.11062 7.29783C7.78046 7.62641 7.61538 8.02545 7.61538 8.49495C7.61538 8.96437 7.77971 9.36416 8.10838 9.69433C8.43696 10.0244 8.83596 10.1895 9.30538 10.1895Z"
                  fill="black"
                />
              </g>
            </svg>

          </div>

          {/* Video */}
          <div className="text-[12px] leading-[16px] text-[#707070]">
            {route.video ? `Video #${route.video}` : "No video"}
          </div>

          {/* Date / Time */}
          <div className="flex items-center gap-[10px] text-[12px] leading-[16px] text-[#707070]">
            <span>{dateStr}</span>
            <span className="w-[15px] border-t border-[#C8C6C5] rotate-90" />
            <span>{timeStr}</span>
          </div>

          {/* Download (fits inside because it’s part of the same fixed content area) */}
          <a
            href={route.file}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[12px] leading-[16px] text-[#5937D6] font-semibold hover:underline"
          >
            Download GPX →
          </a>
        </div>

      </div>
    </div>
  );
}

export default function RoutesMapPage() {
  const { data, isLoading, isError, error } = useFetchRouteMapData();
  const { mutateAsync: uploadRoute, isPending: isUploading } = useUploadRoute();

  const location = useLocation();
  const navigate = useNavigate();

  const routes = useMemo(() => {
    return Array.isArray(data) ? data : [];
  }, [data]);

  const [hoveredRoute, setHoveredRoute] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);

  const [popupRoute, setPopupRoute] = useState(null);
  const [popupLatLng, setPopupLatLng] = useState(null); // Leaflet LatLng

  const [routeName, setRouteName] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const mode = params.get("mode");
    const heatmapParam = params.get("heatmap"); // "1" | "true" | null

    const wantsHeatmap = heatmapParam === "1" || heatmapParam === "true";

    if (mode === "plan") {
      // planning mode always disables heatmap
      setIsHeatmapMode(false);
      setIsPlanningMode(true);
      resetBrowseSelectionState();
      resetPlanningState();
      setActiveFilter(null);
      setCalendarOpenFor(null);
    } else {
      setIsPlanningMode(false);
      resetPlanningState();

      // ✅ auto enable/disable based on query param
      setIsHeatmapMode(wantsHeatmap);

      // optional: clear selection/popup when landing in heatmap mode
      if (wantsHeatmap) {
        resetBrowseSelectionState();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Heatmap state
  const [isHeatmapMode, setIsHeatmapMode] = useState(false);

  const toggleHeatmap = () => {
    setIsHeatmapMode((prev) => {
      const next = !prev;

      // close popups + selection when switching modes (prevents odd UI states)
      setPopupRoute(null);
      setPopupLatLng(null);
      setSelectedRoute(null);
      setHoveredRoute(null);
      setHoveredRouteCenter(null);

      // if heatmap is being enabled, ensure planning is off
      if (next) {
        setIsPlanningMode(false);
        setPlannedRouteData(null);
        setShowSaveModal(false);
      }

      return next;
    });

    // close dropdowns too
    setActiveFilter(null);
    setCalendarOpenFor(null);
  };

  // Bike icons for markers
  const bikeIcon = useMemo(() => {
  const svg = `
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g filter="url(#filter0_d_1416_3928)">
      <rect x="4" y="2" width="32" height="32" rx="16" fill="#FFFAFC"/>
      <mask id="mask0_1416_3928" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="12" y="10" width="16" height="16">
        <rect x="12" y="10" width="16" height="16" fill="#D9D9D9"/>
      </mask>
      <g mask="url(#mask0_1416_3928)">
        <path d="M15.5865 24.2553C14.7771 24.2553 14.088 23.9701 13.5192 23.3996C12.9504 22.8292 12.666 22.1392 12.666 21.3298C12.666 20.5203 12.9512 19.8312 13.5217 19.2625C14.0922 18.6937 14.7822 18.4093 15.5917 18.4093C16.401 18.4093 17.0901 18.6945 17.659 19.265C18.2278 19.8354 18.5122 20.5253 18.5122 21.3348C18.5122 22.1442 18.2269 22.8333 17.6563 23.4021C17.0859 23.9709 16.396 24.2553 15.5865 24.2553ZM15.586 23.5886C16.212 23.5886 16.7451 23.3696 17.1852 22.9315C17.6254 22.4933 17.8455 21.9613 17.8455 21.3353C17.8455 20.7094 17.6264 20.1763 17.1882 19.7361C16.7501 19.296 16.2181 19.076 15.5922 19.076C14.9662 19.076 14.4331 19.295 13.993 19.7331C13.5528 20.1712 13.3327 20.7033 13.3327 21.3293C13.3327 21.9552 13.5517 22.4882 13.9898 22.9285C14.4281 23.3686 14.9601 23.5886 15.586 23.5886ZM20.7993 14.9375L18.8147 16.922L20.0302 18.2003C20.1267 18.2968 20.2013 18.4101 20.2538 18.54C20.3064 18.6698 20.3327 18.8023 20.3327 18.9375V21.999C20.3327 22.0934 20.3007 22.1726 20.2367 22.2365C20.1727 22.3003 20.0934 22.3323 19.9988 22.3323C19.9043 22.3323 19.8252 22.3003 19.7615 22.2365C19.6978 22.1726 19.666 22.0934 19.666 21.999V19.0375L17.3532 17.2861C17.2541 17.1981 17.1776 17.0938 17.1237 16.9733C17.0699 16.8528 17.043 16.7262 17.043 16.5933C17.043 16.4603 17.0706 16.3367 17.1257 16.2226C17.1808 16.1085 17.2566 16.0032 17.3532 15.9066L19.2968 13.9631C19.396 13.8639 19.5122 13.7874 19.6457 13.7336C19.7792 13.6797 19.9192 13.6528 20.0657 13.6528C20.212 13.6528 20.3521 13.6797 20.4858 13.7336C20.6196 13.7874 20.7361 13.8639 20.8352 13.9631L22.1018 15.2298C22.3845 15.5095 22.6948 15.7375 23.0327 15.9138C23.3706 16.0901 23.73 16.2087 24.1108 16.2695C24.2057 16.2857 24.2825 16.3318 24.341 16.408C24.3996 16.484 24.4207 16.5694 24.4045 16.6643C24.3883 16.7592 24.3421 16.8359 24.266 16.8945C24.1899 16.953 24.1045 16.9742 24.0097 16.958C23.5352 16.8862 23.0914 16.7413 22.6782 16.5233C22.265 16.3054 21.8934 16.0315 21.5635 15.7015L20.7993 14.9375ZM22.0763 13.5886C21.7951 13.5886 21.5581 13.4923 21.3653 13.2996C21.1727 13.1068 21.0763 12.8698 21.0763 12.5886C21.0763 12.3075 21.1727 12.0706 21.3653 11.8778C21.5581 11.685 21.7951 11.5886 22.0763 11.5886C22.3575 11.5886 22.5944 11.685 22.7872 11.8778C22.98 12.0706 23.0763 12.3075 23.0763 12.5886C23.0763 12.8698 22.98 13.1068 22.7872 13.2996C22.5944 13.4923 22.3575 13.5886 22.0763 13.5886ZM24.407 24.2553C23.5977 24.2553 22.9086 23.9701 22.3397 23.3996C21.7709 22.8292 21.4865 22.1392 21.4865 21.3298C21.4865 20.5203 21.7718 19.8312 22.3424 19.2625C22.9128 18.6937 23.6027 18.4093 24.4122 18.4093C25.2216 18.4093 25.9107 18.6945 26.4795 19.265C27.0483 19.8354 27.3327 20.5253 27.3327 21.3348C27.3327 22.1442 27.0475 22.8333 26.477 23.4021C25.9065 23.9709 25.2165 24.2553 24.407 24.2553ZM24.4065 23.5886C25.0325 23.5886 25.5656 23.3696 26.0057 22.9315C26.4459 22.4933 26.666 21.9613 26.666 21.3353C26.666 20.7094 26.447 20.1763 26.0088 19.7361C25.5706 19.296 25.0386 19.076 24.4127 19.076C23.7867 19.076 23.2536 19.295 22.8135 19.7331C22.3733 20.1712 22.1532 20.7033 22.1532 21.3293C22.1532 21.9552 22.3723 22.4882 22.8105 22.9285C23.2486 23.3686 23.7806 23.5886 24.4065 23.5886Z" fill="#C60A5F"/>
      </g>
    </g>
    <defs>
      <filter id="filter0_d_1416_3928" x="0" y="0" width="40" height="40" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset dy="2"/>
        <feGaussianBlur stdDeviation="2"/>
        <feComposite in2="hardAlpha" operator="out"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.25 0"/>
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_1416_3928"/>
        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_1416_3928" result="shape"/>
      </filter>
    </defs>
  </svg>`;

  return L.divIcon({
    className: "", // important: no default leaflet styles
    html: svg,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}, []);

  // === Filters (RouteList-style) ===
  const [searchTags, setSearchTags] = useState("");

  // single thumb slider (sets maxDistance)
  const DIST_MIN = 0;
  const DIST_MAX = 100;
  const [distanceKm, setDistanceKm] = useState(0);

  // ISO date strings (YYYY-MM-DD)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [activeFilter, setActiveFilter] = useState(null);

  const filterRef = useRef(null);
  const tagsDropdownRef = useRef(null);

  // calendar state
  const today = new Date();
  const [calendarOpenFor, setCalendarOpenFor] = useState(null); // "start" | "end" | null
  const [calendarMonth, setCalendarMonth] = useState(today.getMonth());
  const [calendarYear, setCalendarYear] = useState(today.getFullYear());
  const calendarDropdownRef = useRef(null);
  const startCalendarTriggerRef = useRef(null);
  const endCalendarTriggerRef = useRef(null);
  const [calendarPosition, setCalendarPosition] = useState({ top: 0, left: 0 });

  const [hoveredRouteCenter, setHoveredRouteCenter] = useState(null);

  const calendarMatrix = useMemo(
    () => buildCalendarMatrix(calendarYear, calendarMonth),
    [calendarMonth, calendarYear]
  );

  const parsedStartDate = parseIsoDate(startDate);
  const parsedEndDate = parseIsoDate(endDate);

  const activeMonthLabel = useMemo(
    () => new Date(calendarYear, calendarMonth).toLocaleString("en-US", { month: "long" }),
    [calendarYear, calendarMonth]
  );

  const formatCalendarInput = (value) => {
    if (!value) return "";
    const parsed = parseIsoDate(value);
    return parsed ? parsed.toLocaleDateString("en-GB") : "";
  };

  const uniqueTags = useMemo(() => {
    const defaultTags = ["hot", "recommended", "new", "easy", "difficult", "urban"];
    const s = new Set(defaultTags);
    (routes || []).forEach((r) => (r.tags || []).forEach((t) => t && s.add(t)));
    return Array.from(s);
  }, [routes]);

  const handleCalendarNav = (direction) => {
    const next = new Date(calendarYear, calendarMonth + direction);
    setCalendarYear(next.getFullYear());
    setCalendarMonth(next.getMonth());
  };

  const openCalendar = (target) => {
    setActiveFilter(null);

    if (calendarOpenFor === target) {
      setCalendarOpenFor(null);
      return;
    }

    const triggerEl = target === "start" ? startCalendarTriggerRef.current : endCalendarTriggerRef.current;
    const containerEl = filterRef.current;

    if (triggerEl && containerEl) {
      const triggerRect = triggerEl.getBoundingClientRect();
      const containerRect = containerEl.getBoundingClientRect();

      const overlayWidth = 222;
      const padding = 16;
      const desiredLeft = triggerRect.left - containerRect.left;
      const maxLeft = Math.max(padding, containerRect.width - overlayWidth - padding);
      const clampedLeft = Math.max(padding, Math.min(desiredLeft, maxLeft));

      setCalendarPosition({
        top: triggerRect.bottom - containerRect.top + 8,
        left: clampedLeft,
      });
    }

    const referenceIso = target === "start" ? startDate : endDate;
    const fallbackIso = startDate || endDate;
    const referenceDate = parseIsoDate(referenceIso) || parseIsoDate(fallbackIso) || new Date();
    setCalendarYear(referenceDate.getFullYear());
    setCalendarMonth(referenceDate.getMonth());

    setCalendarOpenFor(target);
  };

  const handleDaySelect = (date) => {
    if (!calendarOpenFor) return;
    const iso = toIsoDate(date);

    if (calendarOpenFor === "start") {
      setStartDate(iso);
      if (endDate && parseIsoDate(endDate) && parseIsoDate(endDate) < date) setEndDate(iso);
    } else {
      setEndDate(iso);
      if (startDate && parseIsoDate(startDate) && parseIsoDate(startDate) > date) setStartDate(iso);
    }

    setCalendarOpenFor(null);
  };

  const clearCalendarSelection = () => {
    setStartDate("");
    setEndDate("");
    setCalendarOpenFor(null);
  };

  useEffect(() => {
    if (!calendarOpenFor) return undefined;

    const onDocClick = (event) => {
      if (
        calendarDropdownRef.current &&
        !calendarDropdownRef.current.contains(event.target) &&
        !startCalendarTriggerRef.current?.contains(event.target) &&
        !endCalendarTriggerRef.current?.contains(event.target)
      ) {
        setCalendarOpenFor(null);
      }
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [calendarOpenFor]);

  useEffect(() => {
    const onMouseDown = (event) => {
      const target = event.target;

      if (activeFilter === "tags" && tagsDropdownRef.current && !tagsDropdownRef.current.contains(target)) {
        setActiveFilter(null);
        return;
      }

      if (!filterRef.current) return;
      if (!filterRef.current.contains(target)) setActiveFilter(null);
    };

    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [activeFilter]);

  const clearFilters = () => {
    setSearchTags("");
    setStartDate("");
    setEndDate("");
    setDistanceKm(0);
    setCalendarOpenFor(null);
    setActiveFilter(null);
  };

  // Route planner state
  const [isPlanningMode, setIsPlanningMode] = useState(false);
  const [plannedRouteData, setPlannedRouteData] = useState(null);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // Unsaved changes = you have a planned route (>=2 waypoints) and you haven't saved/exited yet
  const hasUnsavedPlanningChanges =
    isPlanningMode && !!plannedRouteData?.coordinates?.length && (plannedRouteData?.waypoints?.length ?? 0) >= 2;

  const canSavePlanned = !!plannedRouteData?.coordinates?.length && (plannedRouteData?.waypoints?.length ?? 0) >= 2;

  useEffect(() => {
    if (!showExitConfirm) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowExitConfirm(false);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showExitConfirm]);

  // Filtered routes
    const filteredRoutes = useMemo(() => {
    return routes.filter((route) => {
      // Tags filter (single value in searchTags)
      if (searchTags.trim()) {
        const searchTerms = searchTags
          .toLowerCase()
          .split(",")
          .map((term) => term.trim())
          .filter(Boolean);

        const routeTags = (route.tags || []).map((tag) => String(tag).toLowerCase());
        const tagMatches = searchTerms.some((term) =>
          routeTags.some((routeTag) => routeTag.includes(term) || term.includes(routeTag))
        );
        if (!tagMatches) return false;
      }

      // Distance
      const dist = Number(route.distance_km ?? 0);

      // If distanceKm === 0 => treat as "All distances"
      if (distanceKm > 0 && dist > distanceKm) return false;

      // Date range (uploaded_at)
      if (startDate) {
        const routeDate = new Date(route.uploaded_at);
        const filterStart = new Date(`${startDate}T00:00:00`);
        if (routeDate < filterStart) return false;
      }

      if (endDate) {
        const routeDate = new Date(route.uploaded_at);
        const filterEnd = new Date(`${endDate}T23:59:59.999`);
        if (routeDate > filterEnd) return false;
      }

      return true;
    });
  }, [routes, searchTags, distanceKm, startDate, endDate]);

  const hasFilters = Boolean(searchTags.trim() || distanceKm > 0 || startDate || endDate);
  const emptyMessage = routes.length === 0 ? "No routes found." : hasFilters ? "No routes match your filters." : "No routes found.";

  // Route planner handlers
  const resetPlanningState = () => {
    setPlannedRouteData(null);
    setShowSaveModal(false);
    setShowExitConfirm(false);
  };

  const resetBrowseSelectionState = () => {
    setSelectedRoute(null);
    setPopupRoute(null);
    setPopupLatLng(null);
    setHoveredRoute(null);
    setHoveredRouteCenter(null);
  };

  const exitPlanning = () => {
    setIsPlanningMode(false);
    resetPlanningState();
    resetBrowseSelectionState();
    setShowExitConfirm(false);

    const params = new URLSearchParams(location.search);
    params.delete("mode");
    navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
  };

  const handleExitPlanningClick = () => {
    if (hasUnsavedPlanningChanges) {
      setShowExitConfirm(true);
      return;
    }
    exitPlanning();
  };

  const handleRouteCreated = (routeData) => {
    console.log('Route created:', routeData);
    setPlannedRouteData(routeData);
  };

  const handleOpenSaveModal = () => {
    if (!canSavePlanned) return;
    setShowSaveModal(true);
  };

  const handleSaveRoute = async () => {
    if (!plannedRouteData) return;

    try {
      const gpxData = generateGPX(plannedRouteData);
      
      // Create a File object from the GPX data
      const blob = new Blob([gpxData], { type: 'application/gpx+xml' });
      const file = new File([blob], `planned_route_${Date.now()}.gpx`, {
        type: 'application/gpx+xml',
      });

      // Upload to S3 using the same flow as uploading a route
      await uploadRoute({ file, videoId: null, name: routeName });
      exitPlanning();
      
    } catch (err) {
      console.error('Error saving route:', err);
      alert('Failed to save route. Please try again.');
    }
  };

  const generateGPX = (routeData) => {
    const { coordinates } = routeData;

    const trackPoints = coordinates
      .map(
        ([lat, lng]) =>
          `      <trkpt lat="${lat}" lon="${lng}"></trkpt>`,
      )
      .join('\n');

    return `<?xml version="1.0" encoding="UTF-8"?>
      <gpx version="1.1" creator="Camber Route Planner" xmlns="http://www.topografix.com/GPX/1/1">
        <metadata>
          <name>Planned Route</name>
          <time>${new Date().toISOString()}</time>
        </metadata>
        <trk>
          <name>Planned Route</name>
          <trkseg>
      ${trackPoints}
          </trkseg>
        </trk>
      </gpx>`;
  };

  const getRouteStyle = (route) => {
    const isSelected = selectedRoute?.id === route.id;
    const isHovered = hoveredRoute?.id === route.id;

    if (isSelected) {
      return {
        color: '#E91E63',
        weight: 6,
        opacity: 1,
        lineCap: 'round',
        lineJoin: 'round',
      };
    }

    if (isHovered) {
      return {
        color: '#F35DA1',
        weight: 8,
        opacity: 1,
        lineCap: 'round',
        lineJoin: 'round',
      };
    }

    return {
      color: '#B75DF3',
      weight: 4,
      opacity: 0.8,
      lineCap: 'round',
      lineJoin: 'round',
    };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner className="h-12 w-12 text-camber-blue-500 mx-auto mb-4" />
          <p className="text-camber-gray-600">Loading routes...</p>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700 font-medium">
            Error loading routes: {error?.message}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      {/* <div className="bg-gradient-to-r from-camber-pink-500 to-camber-blue-500 text-white p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Routes Map</h1>
            <p className="text-camber-pink-100">
              Explore all uploaded routes on an interactive map
            </p>
          </div>
          <button
            onClick={isPlanningMode ? handleCancelPlanning : handleStartPlanning}
            className={`px-6 py-3 rounded-lg font-semibold transition ${
              isPlanningMode
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-white text-camber-blue-600 hover:bg-camber-gray-100'
            }`}
          >
            {isPlanningMode ? '✕ Cancel Planning' : '🗺️ Plan New Route'}
          </button>
        </div>
      </div> */}

      <div className="flex-1 p-6 space-y-6 overflow-auto">
        <div className="space-y-6">
          {/* Planning Mode Banner (keep as-is) */}
          {isPlanningMode && (
            <div className="relative h-[72px]">
              {/* Back icon + label (absolute like Figma) */}
              <button
                type="button"
                onClick={handleExitPlanningClick}
                className="absolute top-[32px] flex items-center gap-[4px] text-[#242424]"
              >
                {/* 16x16 arrow */}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M15 6L9 12L15 18"
                    stroke="#242424"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <span
                  className="font-['Open_Sans'] text-[14px] font-semibold leading-[155%]"
                  style={{ height: 22 }}
                >
                  Exit Route Planning
                </span>
              </button>

              {/* Save button */}
              <button
                type="button"
                onClick={handleOpenSaveModal}
                disabled={!canSavePlanned}
                className="absolute right-[4px] top-[32px] h-[40px] rounded-[4px] bg-[#5937D6] px-[16px] flex items-center justify-center gap-[4px] disabled:opacity-50"
              >
                {/* save icon 24x24 */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M17 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7l-4-4Z"
                    stroke="#FAFAFA"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  />
                  <path d="M7 21V13h10v8" stroke="#FAFAFA" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M7 3v4h8" stroke="#FAFAFA" strokeWidth="2" strokeLinejoin="round" />
                </svg>

                <span className="font-['Open_Sans'] text-[14px] font-normal leading-[155%] text-[#FAFAFA]">
                  Save Route
                </span>
              </button>
            </div>
          )}

          {/* Tip bar (Figma top) */}
          {!isPlanningMode && (
            <div className="bg-[#F3F5FE] border border-[#C9D8FC] rounded-md px-4 py-2 flex items-center gap-2">
              <span className="text-[#333]">ⓘ</span>
              <p className="text-sm font-semibold text-[#333]">
                  Tip: Select a route on the map to view details. Hover over to highlight it
              </p>
            </div>
          )}

          {/* Map Shell */}
          <div className="relative">
            {/* Floating filters toolbar (Figma overlay) */}
            {!isPlanningMode && (
              <div className="absolute left-1/2 top-4 -translate-x-1/2 z-[3000] w-[min(1113px,calc(100%-24px))]">
                <div
                  ref={filterRef}
                  className="relative flex flex-wrap items-center justify-center gap-[8px] drop-shadow-[0px_2px_6.3px_rgba(0,0,0,0.16)]"
                >
                  {/* Heatmap button (placeholder) */}
                  <button
                    type="button"
                    onClick={toggleHeatmap}
                    className={
                      isHeatmapMode
                        ? "w-[132px] h-[32px] bg-[#FFF1FE] border border-[#D27ECE] rounded-[4px] px-[8px] flex items-center justify-center text-[12px] leading-[155%] text-[#5F145B]"
                        : "w-[132px] h-[32px] bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] flex items-center justify-center text-[12px] leading-[155%] text-[#4F4F4F]"
                    }
                  >
                    Heatmap
                  </button>

                  {/* Tags (copied from RouteList) */}
                  <div ref={tagsDropdownRef} className="relative w-[130px] h-[32px]">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCalendarOpenFor(null);
                        setActiveFilter((prev) => (prev === "tags" ? null : "tags"));
                      }}
                      className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[10px] flex items-center justify-between min-w-0"
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-[12px] leading-[155%] text-[#939393] shrink-0">Tags</span>
                        <span
                          className={`text-[12px] leading-[155%] min-w-0 flex-1 truncate ${
                            searchTags ? "text-[#333333]" : "text-[#939393]"
                          }`}
                          title={searchTags || ""}
                        >
                          {searchTags || ""}
                        </span>
                      </div>

                      <span className="ml-2 flex items-center justify-center shrink-0 text-[#939393]">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M7 10L12 15L17 10" fill="currentColor" />
                        </svg>
                      </span>
                    </button>

                    {activeFilter === "tags" && (
                      <div className="absolute left-0 top-[32px] w-[128px] bg-white border-t border-[#E0E2E5] rounded-[4px] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] z-20">
                        <div className="max-h-[160px] overflow-y-auto">
                          <div className="flex flex-col items-center justify-center px-[7px] py-[8px] gap-[8px]">
                            <button
                              type="button"
                              onClick={() => {
                                setSearchTags("");
                                setActiveFilter(null);
                              }}
                              className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%]"
                              role="option"
                              aria-selected={searchTags === ""}
                            >
                              All
                            </button>

                            {uniqueTags.length > 0 ? (
                              uniqueTags.map((t) => (
                                <React.Fragment key={t}>
                                  <div className="w-full border-t border-[#E0E2E5]" />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSearchTags(t);
                                      setActiveFilter(null);
                                    }}
                                    className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%] truncate"
                                    role="option"
                                    aria-selected={searchTags === t}
                                    title={t}
                                  >
                                    {t}
                                  </button>
                                </React.Fragment>
                              ))
                            ) : (
                              <>
                                <div className="w-full border-t border-[#E0E2E5]" />
                                <div className="w-full h-[19px] flex items-center justify-center text-[#0B0B0B] text-[12px] leading-[155%]">
                                  No tags
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Distance dropdown + Range slider (copied from RouteList) */}
                  <div className="relative w-[148px] h-[32px]">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCalendarOpenFor(null);
                        setActiveFilter((prev) => (prev === "distance" ? null : "distance"));
                      }}
                      className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[10px] flex items-center justify-between min-w-0"
                      aria-haspopup="dialog"
                      aria-expanded={activeFilter === "distance"}
                    >
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-[12px] leading-[155%] text-[#939393] shrink-0">Distance</span>
                        <span className="text-[12px] leading-[155%] min-w-0 flex-1 truncate text-[#333333]">
                          {distanceKm} km
                        </span>
                      </div>

                      <span className="ml-2 flex items-center justify-center shrink-0 text-[#939393]">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M7 10L12 15L17 10" fill="currentColor" />
                        </svg>
                      </span>
                    </button>

                    {activeFilter === "distance" && (
                      <div className="absolute left-0 top-[36px] w-[260px] bg-white border border-[#E0E2E5] rounded-[10px] shadow-[0px_2px_10px_rgba(0,0,0,0.18)] z-20 px-4 py-4">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <Range
                              values={[distanceKm]}
                              step={1}
                              min={DIST_MIN}
                              max={DIST_MAX}
                              onChange={(values) => {
                                const v = values[0] ?? 0;
                                setDistanceKm(v);
                              }}
                              renderTrack={({ props, children }) => {
                                const pct = ((distanceKm - DIST_MIN) / (DIST_MAX - DIST_MIN)) * 100;
                                return (
                                  <div onMouseDown={props.onMouseDown} onTouchStart={props.onTouchStart} className="w-full h-[11px] flex">
                                    <div ref={props.ref} className="relative w-full h-[6px] bg-[#D9D9D9] rounded-[55px] self-center">
                                      <div
                                        className="absolute inset-y-0 left-0 rounded-[55px]"
                                        style={{
                                          width: `${pct}%`,
                                          backgroundImage: "linear-gradient(180deg, #DA3272 0%, #5438CE 100%)",
                                        }}
                                      />
                                      {children}
                                    </div>
                                  </div>
                                );
                              }}
                              renderThumb={({ props }) => {
                                const { key, ...rest } = props;
                                return (
                                  <div key={key} {...rest} className="w-[16px] h-[16px] rounded-full outline-none"
                                  style={{
                                    ...props.style,
                                    backgroundImage: "linear-gradient(180deg, #DA3272 0%, #5438CE 100%)",
                                    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.25)",
                                  }} />
                                );
                              }}
                            />
                          </div>

                          <div className="w-[52px] text-right text-[14px] leading-[155%] text-[#AAACAF]">
                            {distanceKm} km
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Start Date trigger */}
                  <div ref={startCalendarTriggerRef} className="relative w-[129px] h-[32px]">
                    <button
                      type="button"
                      onClick={() => openCalendar("start")}
                      aria-haspopup="true"
                      aria-expanded={calendarOpenFor === "start"}
                      className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between text-[12px] leading-[155%] text-[#333333]"
                    >
                      <span className={startDate ? "text-[#0B0B0B]" : "text-[#9A9A9A]"}>
                        {startDate ? formatCalendarInput(startDate) : "Start Date"}
                      </span>
                      <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M7 10L12 15L17 10" fill="currentColor" />
                        </svg>
                      </span>
                    </button>
                  </div>

                  {/* End Date trigger */}
                  <div ref={endCalendarTriggerRef} className="relative w-[123px] h-[32px]">
                    <button
                      type="button"
                      onClick={() => openCalendar("end")}
                      aria-haspopup="true"
                      aria-expanded={calendarOpenFor === "end"}
                      className="w-full h-full bg-white border border-[#E0E2E5] rounded-[4px] px-[8px] py-[4px] flex items-center justify-between text-[12px] leading-[155%] text-[#333333]"
                    >
                      <span className={endDate ? "text-[#0B0B0B]" : "text-[#9A9A9A]"}>
                        {endDate ? formatCalendarInput(endDate) : "End Date"}
                      </span>
                      <span className="w-[24px] h-[24px] flex items-center justify-center text-[#939393]">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                          <path d="M7 10L12 15L17 10" fill="currentColor" />
                        </svg>
                      </span>
                    </button>
                  </div>

                  {/* Calendar dropdown overlay (exact) */}
                  {calendarOpenFor && (
                    <div
                      ref={calendarDropdownRef}
                      className="absolute z-30 w-[222px] h-[249px] bg-white border border-[#E0E2E5] shadow-[0px_2px_4.3px_rgba(0,0,0,0.16)] rounded-[4px]"
                      style={{ top: calendarPosition.top, left: calendarPosition.left }}
                    >
                      {/* Header */}
                      <div className="flex flex-col items-start px-[14px] py-[10px] gap-[8px]">
                        <div className="w-full h-[22px] flex items-center justify-between">
                          <div className="text-[16px] leading-[22px] font-normal text-[#333333]">
                            {activeMonthLabel}
                          </div>

                          <div className="h-[16px] flex items-center justify-center gap-[4px]">
                            <button type="button" onClick={() => handleCalendarNav(-1)} className="w-[11px] h-[11px] flex items-center justify-center" aria-label="Previous month">
                              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                                <path d="M7.5 2.5L4.5 6L7.5 9.5" stroke="#BBBABB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>

                            <div className="w-[28px] text-center text-[12px] leading-[16px] font-normal text-[#333333]">
                              {calendarYear}
                            </div>

                            <button type="button" onClick={() => handleCalendarNav(1)} className="w-[11px] h-[11px] flex items-center justify-center" aria-label="Next month">
                              <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                                <path d="M4.5 9.5L7.5 6L4.5 2.5" stroke="#BBBABB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Grid */}
                      <div className="w-full flex flex-col items-start px-[7px]">
                        <div className="w-[208px] h-[32px] flex items-start justify-between opacity-70">
                          {calendarWeekdayLabels.map((label, idx) => (
                            <div key={`${label}-${idx}`} className="w-[30px] h-[32px] p-[4px] flex items-start justify-center">
                              <div className="w-[22px] h-[24px] flex items-center justify-center">
                                <span className="text-[10px] leading-[14px] font-normal text-[#333333]">
                                  {label.toLowerCase()}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div className="w-[208px] flex flex-col items-start">
                          {calendarMatrix.map((week, weekIndex) => (
                            <div key={weekIndex} className="w-[208px] h-[32px] flex items-start justify-between">
                              {week.map((day, dayIndex) => {
                                const isSelectedStart = isSameDay(day.date, parsedStartDate);
                                const isSelectedEnd = isSameDay(day.date, parsedEndDate);
                                const inRange = isWithinRange(day.date, parsedStartDate, parsedEndDate);
                                const isSelected = isSelectedStart || isSelectedEnd;
                                const cellOpacity = day.inMonth ? "" : "opacity-30";

                                const pillClass = isSelected
                                  ? "bg-gradient-to-b from-[#DA3272] to-[#5438CE] text-white"
                                  : inRange
                                    ? "bg-[#F3E8FF] text-[#333333]"
                                    : "bg-transparent text-[#333333]";

                                return (
                                  <button
                                    key={`${weekIndex}-${dayIndex}`}
                                    type="button"
                                    onClick={() => handleDaySelect(day.date)}
                                    className={`w-[30px] h-[32px] p-[4px] flex items-start justify-center ${cellOpacity}`}
                                  >
                                    <span
                                      className={`${isSelected ? "w-[22px] h-[22px]" : "w-[22px] h-[24px]"} rounded-[99px] flex items-center justify-center ${pillClass}`}
                                    >
                                      <span className={`text-[10px] leading-[14px] font-normal ${isSelected ? "text-white" : ""}`}>
                                        {day.label.padStart(2, "0")}
                                      </span>
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="px-[14px] py-[10px] flex items-center justify-start">
                        <button type="button" onClick={clearCalendarSelection} className="text-[12px] leading-[16px] text-[#5937D6]">
                          Clear
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Clear (right) */}
                  {(searchTags || distanceKm > 0 || startDate || endDate) && (
                    <button
                      type="button"
                      onClick={clearFilters}
                      className="ml-2 h-[32px] px-3 bg-white border border-[#E0E2E5] rounded-[4px] text-[12px] leading-[155%] font-semibold text-[#5937D6] hover:underline"
                    >
                      Clear
                    </button>
                  )}
                </div>

                {/* “Showing X of Y” below like your screenshot */}
                {/* <div className="mt-2 text-xs text-camber-gray-600 text-center">
                  Showing <strong>{filteredRoutes.length}</strong> of <strong>{routes.length}</strong> routes
                </div> */}
              </div>
            )}

            {isPlanningMode && (
              <div
                className="absolute left-1/2 -translate-x-1/2 z-[4000] w-[494px] max-w-[calc(100%-24px)]
                          h-[40px] top-[6px]
                          bg-[#F2FDF4] border border-[#72B581]
                          shadow-[0px_2px_4.8px_rgba(0,0,0,0.16)] rounded-[4px]
                          px-[15px] py-[8px] flex items-center gap-[8px]"
              >
                {/* info icon 24x24 */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M12 17v-6"
                    stroke="#2C5535"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M12 8h.01"
                    stroke="#2C5535"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="12" r="9" stroke="#2C5535" strokeWidth="2" />
                </svg>

                <span className="font-['Open_Sans'] text-[12px] font-normal leading-[155%] text-[#2C5535]">
                  Click on the map to add waypoints.The route will be calculated automatically.
                </span>
              </div>
            )}

            {/* Map Container (rounded 8px, Figma height ~630px) */}
            <div className="rounded-lg overflow-hidden border border-camber-gray-200 shadow-lg bg-white">
              {filteredRoutes.length === 0 && !isPlanningMode ? (
                <div className="h-[630px] flex items-center justify-center">
                  <div className="flex flex-col items-center gap-[16px]">
                    <EmptyRoutesIcon />
                    <div className="text-[12px] leading-[155%] text-[#969696] text-center">
                      {emptyMessage}
                    </div>
                  </div>
                </div>
              ) : (
                <MapContainer
                  center={[0, 0]}
                  zoom={2}
                  style={{ height: '630px', width: '100%' }}
                  scrollWheelZoom={true}
                >
                  <TileLayer
                    url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution="&copy; OpenStreetMap contributors"
                  />

                  {/* Heatmap overlay uses same map instance */}
                  <HeatmapOverlay enabled={isHeatmapMode} />

                  {/* Keep marker only in routes mode */}
                  {!isHeatmapMode && hoveredRoute && hoveredRouteCenter && (
                    <Marker position={hoveredRouteCenter} icon={bikeIcon} interactive={false} zIndexOffset={1000} />
                  )}

                  {/* Fit bounds only in routes mode (otherwise it will fight your viewport while heatmap reloads) */}
                  {!isPlanningMode && !isHeatmapMode && <FitBoundsToRoutes routes={filteredRoutes} />}

                  <RoutePlanner 
                    isActive={isPlanningMode} 
                    onRouteCreated={handleRouteCreated} 
                    onReset={() => setPlannedRouteData(null)}
                  />

                  {/* Routes only when not heatmap */}
                  {!isPlanningMode && !isHeatmapMode &&
                    filteredRoutes.map(
                      (route) =>
                        route.geom && (
                          <GeoJSON
                            key={`${route.id}-${selectedRoute?.id === route.id}-${hoveredRoute?.id === route.id}`}
                            data={route.geom}
                            style={getRouteStyle(route)}
                            eventHandlers={{
                              mouseover: (e) => {
                                setHoveredRoute(route);
                                setHoveredRouteCenter([e.latlng.lat, e.latlng.lng]);
                              },
                              mouseout: () => {
                                setHoveredRoute(null);
                                setHoveredRouteCenter(null);
                              },
                              click: (e) => {
                                setSelectedRoute(route);
                                setPopupRoute(route);
                                setPopupLatLng(e.latlng);
                              },
                            }}
                          >
                            <RouteMiniPopup
                              route={popupRoute}
                              latlng={popupLatLng}
                              onClose={() => {
                                setPopupRoute(null);
                                setSelectedRoute(null);
                                setPopupLatLng(null);
                              }}
                            />
                          </GeoJSON>
                        )
                    )
                  }

                </MapContainer>
              )}
            </div>
          </div>

          {/* Selected Route Info (keep as-is) */}
          {/* {!isPlanningMode && selectedRoute && (
            <div className="bg-camber-blue-50 border-2 border-camber-blue-300 rounded-lg p-4 shadow-md">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-camber-blue-900 mb-2">
                    Selected: Route #{selectedRoute.id}
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    {selectedRoute.distance_km && (
                      <p>
                        <strong>Distance:</strong>{' '}
                        {selectedRoute.distance_km.toFixed(2)} km
                      </p>
                    )}
                    {selectedRoute.duration_seconds && (
                      <p>
                        <strong>Duration:</strong>{' '}
                        {Math.floor(selectedRoute.duration_seconds / 60)} min
                      </p>
                    )}
                    {selectedRoute.video && (
                      <p>
                        <strong>Linked Video:</strong> #{selectedRoute.video}
                      </p>
                    )}
                    <p>
                      <strong>Uploaded:</strong>{' '}
                      {new Date(selectedRoute.uploaded_at).toLocaleString()}
                    </p>
                  </div>
                  {selectedRoute.tags && selectedRoute.tags.length > 0 && (
                    <div className="mt-3">
                      <strong className="text-sm">Tags:</strong>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {selectedRoute.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-camber-blue-200 text-camber-blue-900 text-xs px-2 py-1 rounded font-medium"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setSelectedRoute(null)}
                  className="ml-4 text-camber-blue-600 hover:text-camber-blue-800 font-medium text-sm"
                >
                  ✕ Close
                </button>
              </div>
            </div>
          )} */}
        </div>
      </div>

      {/* Save Route Modal */}
      {showSaveModal && plannedRouteData && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50"
          onClick={() => setShowSaveModal(false)}
        >
          <div
            className="relative w-[435px] max-w-[calc(100%-32px)] rounded-[8px] bg-white px-[24px] pt-[24px] pb-[32px] flex flex-col items-start gap-[24px]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header row */}
            <div className="relative w-full h-[40px]">
              {/* Back button */}
              <button
                type="button"
                onClick={() => setShowSaveModal(false)}
                className="absolute left-0 top-0 w-[40px] h-[40px] rounded-[8px] bg-[#C9D8FC] flex items-center justify-center"
                aria-label="Back"
              >
                {/* arrow_back_ios_new */}
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path
                    d="M15 6L9 12L15 18"
                    stroke="#3C11BA"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Title */}
              <div className="absolute left-[80px] top-[6px] h-[28px] flex items-center">
                <span className="font-['Open_Sans'] text-[18px] font-semibold leading-[155%] text-[#242424]">
                  Save Route
                </span>
              </div>
            </div>

            {/* Body */}
            <div className="w-full flex flex-col items-start gap-[24px]">
              {/* Route details */}
              <div className="w-full flex flex-col items-start gap-[8px]">
                <div className="font-['Open_Sans'] text-[14px] font-normal leading-[155%] text-[#212529]">
                  Route Details
                </div>

                <div className="flex items-center gap-[10px] h-[19px]">
                  <span className="font-['Open_Sans'] text-[14px] font-light leading-[19px] text-[#242424]">
                    {Number(plannedRouteData.distance_km ?? 0).toFixed(0)} km
                  </span>

                  <span className="w-[15px] border-t border-[#C8C6C5] rotate-90" />

                  <span className="font-['Open_Sans'] text-[14px] font-light leading-[19px] text-[#242424]">
                    {Math.floor(Number(plannedRouteData.duration_seconds ?? 0) / 60)} min
                  </span>
                </div>
              </div>

              {/* Name */}
              <div className="w-full flex flex-col items-start gap-[4px]">
                <div className="font-['Open_Sans'] text-[14px] font-normal leading-[155%] text-[#212529]">
                  Name
                </div>

                <div className="w-full h-[40px] rounded-[8px] border border-[#E0E2E5] bg-white px-[12px] flex items-center">
                  <input
                    type="text"
                    value={routeName}
                    onChange={(e) => setRouteName(e.target.value)}
                    placeholder="e.g. Home route"
                    className="w-full outline-none bg-transparent font-['Open_Sans'] text-[14px] leading-[155%] text-[#242424] placeholder:text-[#ADB5BD]"
                  />
                </div>
              </div>

              {/* Buttons */}
              <div className="w-full flex items-center justify-between gap-[16px]">
                <button
                  type="button"
                  onClick={() => setShowSaveModal(false)}
                  disabled={isUploading}
                  className="w-[179px] h-[40px] rounded-[4px] bg-[#E2DCF8] flex items-center justify-center disabled:opacity-50"
                >
                  <span className="font-['Open_Sans'] text-[16px] font-normal leading-[155%] text-[#2C1B6B]">
                    Cancel
                  </span>
                </button>

                <button
                  type="button"
                  onClick={handleSaveRoute}
                  disabled={isUploading || !routeName.trim()}
                  className="w-[180px] h-[40px] rounded-[4px] bg-[#5937D6] flex items-center justify-center disabled:opacity-50"
                >
                  <span className="font-['Open_Sans'] text-[16px] font-normal leading-[155%] text-[#E3DDF8]">
                    {isUploading ? "Saving..." : "Save"}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Exit Planning Confirm Modal */}
      {showExitConfirm && (
        <div
          className="fixed inset-0 z-[10000] flex items-center justify-center"
          onClick={() => setShowExitConfirm(false)}
        >
          {/* overlay */}
          <div className="absolute inset-0 bg-[rgba(19,19,19,0.69)]" />

          {/* dialog */}
          <div
            className="relative w-[312px] h-[148px] bg-[#FDFCFF] rounded-[8px] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="exit-planning-title"
            aria-describedby="exit-planning-desc"
          >
            {/* Text content */}
            <div className="px-[24px] pt-[24px] pb-[8px] flex flex-col gap-[4px]">
              <div
                id="exit-planning-title"
                className="font-['Open_Sans'] text-[14px] font-semibold leading-[19px] text-[#2A2A2A]"
              >
                Exit without saving?
              </div>

              <div
                id="exit-planning-desc"
                className="font-['Open_Sans'] text-[12px] font-normal leading-[16px] text-[#2A2A2A]"
              >
                You have unsaved changes. If you leave now, your changes will be lost.
              </div>
            </div>

            {/* Actions */}
            <div className="mt-auto px-[24px] pb-[24px] flex items-center justify-center gap-[8px]">
              <button
                type="button"
                onClick={() => {
                  setShowExitConfirm(false);
                  exitPlanning();
                }}
                className="w-[98px] h-[34px] rounded-[100px] bg-[#5937D6] flex items-center justify-center"
              >
                <span className="font-['Open_Sans'] text-[14px] font-normal leading-[20px] text-[#E2E2E2]">
                  Exit
                </span>
              </button>

              <button
                type="button"
                onClick={() => setShowExitConfirm(false)}
                className="w-[98px] h-[34px] rounded-[100px] bg-[#F3EEFF] flex items-center justify-center"
              >
                <span className="font-['Open_Sans'] text-[14px] font-normal leading-[20px] text-[#2C2866]">
                  Cancel
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
